import type { Dayjs } from 'dayjs';
import { convertDateLikeToDateTimeISOString } from '../brandedTypes';
import { curry, pipe } from '../effect';
import { pluralize } from '../pluralize';

/** Renders whole seconds as hours/minutes (e.g. `4h 32m`, `34m`). */
export const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
};

/** Renders whole seconds as rounded minutes with a 1-min floor (e.g. `4 min`, `1 min`). */
export const formatMinutes = (seconds: number): string => `${Math.max(1, Math.round(seconds / 60))} min`;

export const formatDurationLong = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours === 0 && minutes === 0) return '0 minutes';

  return [
    hours > 0 ? `${hours} ${pluralize('hour', hours)}` : null,
    minutes > 0 ? `${minutes} ${pluralize('minute', minutes)}` : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' ');
};

export const addHour = (hour: number, date: Dayjs) => date.add(hour, 'hour');

export const addMinute = (minute: number, date: Dayjs) => date.add(minute, 'minute');

export const addSecond = (second: number, date: Dayjs) => date.add(second, 'second');

export const setTime = (date: Dayjs, time: { hour: number; minute: number; second?: number }) =>
  pipe(
    date,
    curry(addHour)(time.hour),
    curry(addMinute)(time.minute),
    curry(addSecond)(time.second ?? 0),
    convertDateLikeToDateTimeISOString,
  );
